<!-- [PROSPEK] 2026-03-06 Ported from main (v4.5.x) — removed upstream in v4.11.1 but required by AudienceBuilder.vue -->
<!-- This component provides filter row input UI (attribute key, operator, value) for the Evo campaign audience builder. -->
<!-- Upstream replaced this with components-next/filter/ConditionRow.vue which has an incompatible interface. -->
<script>
import NextButton from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';

export default {
  name: 'FilterInput',
  components: {
    NextButton,
    TagMultiSelectComboBox,
    MultiSelect,
    SingleSelect,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => {},
    },
    filterAttributes: {
      type: Array,
      default: () => [],
    },
    inputType: {
      type: String,
      default: 'plain_text',
    },
    operators: {
      type: Array,
      default: () => [],
    },
    dropdownValues: {
      type: Array,
      default: () => [],
    },
    showQueryOperator: {
      type: Boolean,
      default: false,
    },
    showUserInput: {
      type: Boolean,
      default: true,
    },
    // [PROSPEK] 2026-06-21 allow checkbox audience rows to hide the operator while preserving model state
    showOperator: {
      type: Boolean,
      default: true,
    },
    // [/PROSPEK]
    groupedFilters: {
      type: Boolean,
      default: false,
    },
    filterGroups: {
      type: Array,
      default: () => [],
    },
    customAttributeType: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
    noOptionsText: {
      type: String,
      default: '',
    },
    contactOptions: {
      type: Array,
      default: () => [],
    },
    contactLoading: {
      type: Boolean,
      default: false,
    },
    contactPlaceholder: {
      type: String,
      default: '',
    },
    contactEmptyState: {
      type: String,
      default: '',
    },
    contactMessage: {
      type: String,
      default: '',
    },
    onContactSearch: {
      type: Function,
      default: null,
    },
  },
  emits: ['update:modelValue', 'removeFilter', 'resetFilter'],
  computed: {
    attributeKey: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.attribute_key;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, attribute_key: value });
      },
    },
    filterOperator: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.filter_operator;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, filter_operator: value });
      },
    },
    values: {
      get() {
        if (!this.modelValue) return null;
        const val = this.modelValue.values;
        if (this.inputType === 'contact_select') {
          return Array.isArray(val) ? val : [];
        }
        return val;
      },
      set(value) {
        const payload = this.modelValue || {};
        const nextValue =
          this.inputType === 'contact_select' && !Array.isArray(value)
            ? []
            : value;
        this.$emit('update:modelValue', { ...payload, values: nextValue });
      },
    },
    query_operator: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.query_operator;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, query_operator: value });
      },
    },
    custom_attribute_type: {
      get() {
        if (!this.customAttributeType) return '';
        return this.customAttributeType;
      },
      set() {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', {
          ...payload,
          custom_attribute_type: this.customAttributeType,
        });
      },
    },
  },
  watch: {
    customAttributeType: {
      handler(value) {
        if (
          value === 'conversation_attribute' ||
          value === 'contact_attribute'
        ) {
          // eslint-disable-next-line vue/no-mutating-props
          this.modelValue.custom_attribute_type = this.customAttributeType;
          // eslint-disable-next-line vue/no-mutating-props
        } else this.modelValue.custom_attribute_type = '';
      },
      immediate: true,
    },
  },
  methods: {
    removeFilter() {
      this.$emit('removeFilter');
    },
    resetFilter() {
      this.$emit('resetFilter');
    },
    getInputErrorClass(errorMessage) {
      return errorMessage
        ? 'bg-n-ruby-8/20 border-n-ruby-5 dark:border-n-ruby-5'
        : 'bg-n-background border-n-weak dark:border-n-weak';
    },
  },
};
</script>

<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div>
    <div
      class="p-2 border border-solid rounded-lg"
      :class="getInputErrorClass(errorMessage)"
    >
      <div class="flex gap-1">
        <select
          v-if="groupedFilters"
          v-model="attributeKey"
          class="max-w-[30%] mb-0 mr-1 !bg-[right_0.5rem_center] !bg-origin-border"
          @change="resetFilter()"
        >
          <optgroup
            v-for="(group, i) in filterGroups"
            :key="i"
            :label="group.name"
          >
            <option
              v-for="attribute in group.attributes"
              :key="attribute.key"
              :value="attribute.key"
              :selected="true"
            >
              {{ attribute.name }}
            </option>
          </optgroup>
        </select>
        <select
          v-else
          v-model="attributeKey"
          class="max-w-[30%] mb-0 mr-1 !bg-[right_0.5rem_center] !bg-origin-border"
          @change="resetFilter()"
        >
          <option
            v-for="attribute in filterAttributes"
            :key="attribute.key"
            :value="attribute.key"
            :disabled="attribute.disabled"
          >
            {{ attribute.name }}
          </option>
        </select>

        <!-- [PROSPEK] 2026-06-21 hide operator control for one-column checkbox audience rows -->
        <select
          v-if="showOperator"
          v-model="filterOperator"
          data-testid="filter-operator-select"
          class="max-w-[20%] mb-0 mr-1 !bg-[right_0.5rem_center] !bg-origin-border"
        >
          <option
            v-for="(operator, o) in operators"
            :key="o"
            :value="operator.value"
          >
            {{ $t(`FILTER.OPERATOR_LABELS.${operator.value}`) }}
          </option>
        </select>
        <!-- [/PROSPEK] -->

        <div v-if="showUserInput" class="flex-grow mr-1 filter__answer--wrap">
          <div
            v-if="inputType === 'contact_select'"
            class="multiselect-wrap--small"
          >
            <TagMultiSelectComboBox
              v-model="values"
              :options="contactOptions"
              :placeholder="
                contactPlaceholder ||
                $t('CAMPAIGN.AUDIENCE_BUILDER.CONTACTS.PLACEHOLDER')
              "
              :search-placeholder="
                contactPlaceholder ||
                $t('CAMPAIGN.AUDIENCE_BUILDER.CONTACTS.SEARCH_PLACEHOLDER')
              "
              :empty-state="
                contactEmptyState ||
                $t('CAMPAIGN.AUDIENCE_BUILDER.CONTACTS.EMPTY')
              "
              :message="contactMessage"
              :has-error="false"
              :disabled="contactLoading"
              @search="value => onContactSearch && onContactSearch(value)"
            />
          </div>
          <div
            v-else-if="inputType === 'multi_select'"
            class="multiselect-wrap--small"
          >
            <MultiSelect
              v-model="values"
              :options="dropdownValues"
              :allow-empty="false"
              :empty-state="noOptionsText"
              dropdown-max-height="max-h-40"
            />
          </div>
          <div
            v-else-if="inputType === 'search_select'"
            class="multiselect-wrap--small"
          >
            <SingleSelect
              v-model="values"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              :options="dropdownValues"
              disable-deselect
              :empty-state="noOptionsText"
              dropdown-max-height="max-h-40"
            />
          </div>
          <div v-else-if="inputType === 'date'" class="multiselect-wrap--small">
            <input
              v-model="values"
              type="date"
              :editable="false"
              class="!mb-0 datepicker"
            />
          </div>
          <input
            v-else-if="inputType === 'number'"
            v-model="values"
            type="number"
            class="!mb-0"
            :placeholder="$t('FILTER.INPUT_PLACEHOLDER')"
          />
          <input
            v-else
            v-model="values"
            type="text"
            class="!mb-0"
            :placeholder="$t('FILTER.INPUT_PLACEHOLDER')"
          />
        </div>
        <NextButton
          icon="i-lucide-x"
          slate
          ghost
          type="button"
          class="flex-shrink-0 ml-auto"
          @click="removeFilter"
        />
      </div>
      <p v-if="errorMessage" class="filter-error">
        {{ errorMessage }}
      </p>
    </div>

    <div
      v-if="showQueryOperator"
      class="flex items-center justify-center relative my-2.5 mx-0"
    >
      <hr class="absolute w-full border-b border-solid border-n-weak" />
      <select
        v-model="query_operator"
        class="relative w-auto mb-0 bg-n-background text-n-slate-12 border-n-weak"
      >
        <option value="and">
          {{ $t('FILTER.QUERY_DROPDOWN_LABELS.AND') }}
        </option>
        <option value="or">
          {{ $t('FILTER.QUERY_DROPDOWN_LABELS.OR') }}
        </option>
      </select>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.filter__answer--wrap {
  input {
    @apply bg-n-background mb-0 text-n-slate-12 border-n-weak;
  }
}

.filter-error {
  @apply text-n-ruby-9 dark:text-n-ruby-9 block my-1 mx-0;
}

.multiselect {
  @apply mb-0;
}
</style>
<!-- [/PROSPEK] -->
